import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { workspacesCreate } from '@/shared/api/generated/workspaces/workspaces';
import { ApiError } from '@/shared/api/http';
import { unwrap } from '@/shared/api/unwrap';
import { slugify } from '@/shared/lib/slugify';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { toast } from '@/shared/ui/Toast';

const schema = z.object({
  name: z.string().trim().min(1, t.profile.nameRequired).max(100),
  slug: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, t.workspace.slugInvalid),
});

type Values = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateWorkspaceDialog({ open, onOpenChange, onCreated }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Форма живёт только пока окно открыто — так её состояние сбрасывается само. */}
      {open && <Body onOpenChange={onOpenChange} onCreated={onCreated} />}
    </Dialog>
  );
}

function Body({ onOpenChange, onCreated }: Omit<Props, 'open'>) {
  const navigate = useNavigate();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', slug: '' },
  });

  const name = form.watch('name');

  // Адрес подсказывается из названия, пока человек не тронул его сам.
  useEffect(() => {
    if (!form.formState.dirtyFields.slug) {
      form.setValue('slug', slugify(name));
    }
  }, [name, form]);

  const submit = form.handleSubmit(async (values) => {
    try {
      const created = unwrap(await workspacesCreate(values));

      toast.success(t.workspace.created);
      onCreated();
      onOpenChange(false);
      await navigate({ to: '/app/w/$slug', params: { slug: created.slug } });
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        form.setError('slug', { message: t.workspace.slugTaken });

        return;
      }

      form.setError('name', {
        message: cause instanceof ApiError ? cause.detail : t.common.unknownError,
      });
    }
  });

  return (
    <DialogContent
      title={t.workspace.createTitle}
      description={t.workspace.createText}
      footer={
        <>
          <Button
            variant="outline"
            tone="neutral"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t.common.cancel}
          </Button>
          <Button type="submit" form="create-workspace" loading={form.formState.isSubmitting}>
            {t.home.create}
          </Button>
        </>
      }
    >
      <form
        id="create-workspace"
        onSubmit={(event) => {
          void submit(event);
        }}
        noValidate
      >
        <Field label={t.workspace.name} required error={form.formState.errors.name?.message}>
          <Input placeholder={t.workspace.namePlaceholder} autoFocus {...form.register('name')} />
        </Field>
        <Field
          label={t.workspace.slug}
          required
          hint={t.workspace.slugHint}
          error={form.formState.errors.slug?.message}
        >
          <Input mono prefix="/app/w/" {...form.register('slug')} />
        </Field>
      </form>
    </DialogContent>
  );
}
